<template>
  <div class="page-container schedule-page">
    <portal to="page-name">Configuração de agenda</portal>
    <div class="loading mt-2" v-if="loading"></div>
    <div class="card card-page" v-else>
      <div class="card-header">
        <div class="card-title">
          Configuração de agenda - {{ getTitle }}
        </div>
      </div>

      <div class="card-body">
        <div class="columns form-group">
          <div
            class="column col-6 col-md-12 form-group"
            :class="{'has-error': $v.form.name.$error}"
          >
            <label for="name" class="form-label">Nome da agenda</label>
            <div class="input-group">
              <input
                type="text"
                id="name"
                name="name"
                class="form-input"
                placeholder="Dr José da Silva / Eletrocardiograma / Mapa 24 horas"
                autocomplete="nope"
                v-model="form.name"
                @change="$v.form.name.$touch()"
              >
            </div>
            <template v-if="$v.form.name.$error">
              <div class="form-input-hint"
                   v-if="!$v.form.name.required">Campo obrigatório</div>
            </template>
          </div>

          <div
            class="column col-3 col-md-6 col-sm-12 form-group"
            :class="{'has-error': $v.form.type.$error}"
          >
            <label for="type" class="form-label">Tipo</label>
            <select id="type" class="form-select" v-model="form.type">
              <option
                v-for="(text, value) in scheduleTypes"
                :value="value"
                :key="value"
              >{{ text }}</option>
            </select>
            <template v-if="$v.form.type.$error">
              <div
                class="form-input-hint"
                v-if="!$v.form.type.required"
              >Campo obrigatório</div>
            </template>
          </div>

          <div class="column col-3 col-md-6 col-sm-12 form-group">
            <label for="place" class="form-label">Local</label>
            <div class="input-group">
              <select id="place" class="form-select" v-model="form.placeId">
                <option value="">[Selecione]</option>
                <option
                  v-for="place in placeOptions"
                  :value="place.id"
                  :key="place.id"
                >{{ place.name }}{{ place.active ? '' : ' (inativo)' }}</option>
              </select>
              <button
                class="btn btn-neutral input-group-btn"
                v-if="canManagePlaces"
                @click="placesModal.show = true"
              >Gerenciar</button>
            </div>
          </div>

          <div class="column col-12 form-group">
            <div class="communication-row">
              <span class="communication-label">Comunicações com o paciente</span>
              <label class="form-switch">
                <input type="checkbox" v-model="form.communication.whatsapp">
                <i class="form-icon"></i>
                <span>WhatsApp</span>
              </label>
              <label class="form-switch">
                <input type="checkbox" v-model="form.communication.email">
                <i class="form-icon"></i>
                <span>E-mail</span>
              </label>
            </div>
          </div>
        </div>

        <schedule-hours
          ref="hours"
          v-if="form.id"
          :schedule-id="form.id"
          @edit="editSchedule"
          @duplicate="duplicateSchedule"
          @add="openSchedule"
        />
        <div class="hours-pending" v-else>
          <fa-icon :icon="['fal', 'clock']" size="2x"/>
          <strong>Os horários entram depois de salvar</strong>
          <span>Preencha o nome e clique em Salvar para configurar os horários.</span>
        </div>
      </div>
      <div class="card-footer">
        <button
          class="btn btn-primary btn-icon btn-icon-left mr-1" @click="save"
          :disabled="saving" :class="{loading: saving}">
          <fa-icon :icon="['fal', 'save']"/>Salvar
        </button>
        <button class="btn btn-gray-outline" @click="$router.back()">
          Voltar
        </button>
      </div>
    </div>
    <schedule-hour-config
      v-if="config.show"
      :show="config.show"
      :schedule-id="form.id"
      :hour-id="config.id"
      :duplicate-id="config.duplicateId"
      @save="onSaveHour"
      @close="config.show = false"
    />
    <places-modal
      v-if="placesModal.show"
      :show="placesModal.show"
      :selected-id="form.placeId"
      @select="form.placeId = $event"
      @changed="loadPlaces"
      @close="placesModal.show = false"
    />
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { types as scheduleTypes } from '@/data/schedule-types';
import { mergeFrom } from '@/helpers/object';
import { resetCache } from '@/helpers/api';
import { LIST_PLACES } from '@/data/actions/modules/general';
import formMixin from 'src/mixins/form';
import ScheduleHourConfig from '@/app/appointment/components/modals/ScheduleHourConfig.vue';
import ScheduleHours from './ScheduleHours.vue';
import PlacesModal from './modals/Places.vue';

export default {
  components: {
    ScheduleHourConfig,
    ScheduleHours,
    PlacesModal,
  },
  mixins: [formMixin],
  data() {
    return {
      path: '/schedules',
      isNew: false,
      loading: false,
      saving: false,
      config: {
        show: false,
        id: null,
        duplicateId: null,
      },
      places: [],
      placesModal: { show: false },
      form: this.blankForm(),
      scheduleTypes,
    };
  },
  mounted() {
    this.init();
  },
  validations() {
    return {
      form: {
        name: { required },
        type: { required },
      },
    };
  },
  computed: {
    canManagePlaces() {
      return this.$can(LIST_PLACES);
    },
    placeOptions() {
      return this.places.filter(place => place.active || place.id === this.form.placeId);
    },
    getTitle() {
      if (!this.form.id) {
        return 'Novo Registro';
      }
      return this.form.name;
    },
  },
  methods: {
    async init() {
      this.isNew = /create$/.test(this.$route.path);

      const promises = [];

      if (!this.isNew) {
        this.form.id = this.$route.params.id;
        promises.push(this.load());
      }
      promises.push(this.loadPlaces());

      this.loading = true;
      await Promise.all(promises);
      this.loading = false;
    },
    async load() {
      try {
        const { data } = await this.$http.get(`${this.path}/${this.form.id}`);

        this.form = mergeFrom(this.blankForm(), {
          ...data,
          placeId: data.place ? data.place.id : '',
        });
      } catch (error) {
        this.$toast.error(error);
      }
    },
    async loadPlaces() {
      try {
        const params = { type: 'clinical', limit: 0 };
        const { data } = await this.$httpX.get('/places', { params });
        this.places = data.items;
      } catch (error) {
        this.$toast.error(error);
      }
    },
    async openSchedule() {
      if (this.saving) {
        return;
      }

      if (!this.form.id) {
        await this.save();
        if (!this.form.id) {
          return;
        }
      }

      this.config = { show: true, id: null, duplicateId: null };
    },
    editSchedule(item) {
      this.config = { show: true, id: item.id, duplicateId: null };
    },
    duplicateSchedule(item) {
      this.config = { show: true, id: null, duplicateId: item.id };
    },
    onSaveHour() {
      resetCache();
      if (this.$refs.hours) this.$refs.hours.reload();
    },
    async save() {
      this.$v.form.$touch();
      if (this.$v.form.$error || this.saving) {
        return;
      }

      this.saving = true;

      const params = {
        name: this.form.name,
      };

      try {
        const { data: found } = await this.$http
          .get(this.path, { params });

        if (found.items.length > 0 && found.items[0].id !== this.form.id) {
          this.$toast.show('Agenda já adicionada', { type: 'error' });
          this.saving = false;
          return;
        }
      } catch (e) {
        this.$toast.show('Erro ao validar a agenda', { type: 'error' });
        this.saving = false;
        return;
      }

      const schedule = this.clone(this.form);

      const request = this.isNew
        ? this.$http.post(this.path, schedule)
        : this.$http.put(`${this.path}/${this.form.id}`, schedule);

      // eslint-disable-next-line consistent-return
      return request
        .then(({ data }) => {
          if (this.isNew) {
            this.$router.replace(`/clinical${this.path}/${data.id}/edit`);
            this.isNew = false;
            this.form.id = data.id;
          }
          this.$toast.show('Registro salvo');
          resetCache();
        })
        .catch(() => {})
        .then(() => {
          this.saving = false;
        });
    },
    blankForm() {
      return {
        id: '',
        name: '',
        type: 'elective',
        placeId: '',
        communication: {
          email: false,
          whatsapp: true,
        },
      };
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
.schedule-page {
  .communication-row {
    align-items: center;
    background-color: lighten($info-color, 48%);
    border: $border-width solid lighten($info-color, 34%);
    border-radius: $border-radius;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-lg;
    padding: $layout-spacing $layout-spacing-lg;
    .communication-label {
      color: $secondary-color-dark;
      font-size: $font-size-xs;
      font-weight: 700;
      letter-spacing: .04em;
      margin-right: $layout-spacing;
      text-transform: uppercase;
    }
    .form-switch {
      margin: 0;
    }
  }
  .hours-pending {
    align-items: center;
    background-color: $gray-color-ultra-light;
    border: $border-width dashed $border-color-dark;
    border-radius: $border-radius;
    color: $gray-color-dark;
    display: flex;
    flex-direction: column;
    font-size: $font-size-xs;
    gap: $layout-spacing-sm;
    margin-top: $layout-spacing-lg;
    padding: $layout-spacing-xl $layout-spacing-lg;
    text-align: center;
    svg {
      color: lighten($primary-color, 20%);
      margin-bottom: $layout-spacing-sm;
    }
    strong {
      color: $secondary-color-dark;
      font-size: $font-size-sm;
    }
  }
}
</style>
